import { DateTime } from 'luxon'
import { useFlag } from '@openfeature/react-sdk'

import { FEATURE_FLAG_KEYS } from '@/flags'

// Zones mirror the backend's `sign-up-promos` parsing (start in Eastern, end at
// end-of-day Hawaii) so the copy shows over roughly the same window the grant
// uses. This is display-only; the server flag remains the source of truth for
// whether a coupon is actually granted.
const START_ZONE = 'America/New_York'
const END_ZONE = 'Pacific/Honolulu'

interface TierCoupon {
  payroll?: string | null
  payrollPromoText?: string
}

interface PromoPeriod {
  name?: string
  startDate?: string
  endDate?: string
  tierCoupons?: Partial<Record<string, TierCoupon | undefined>>
}

const findActivePeriod = (
  flagValue: unknown,
  now: DateTime
): PromoPeriod | null => {
  if (!Array.isArray(flagValue)) return null
  for (const raw of flagValue) {
    const period = raw as PromoPeriod
    if (typeof period?.startDate !== 'string') continue
    if (typeof period?.endDate !== 'string') continue
    const start = DateTime.fromISO(period.startDate, { zone: START_ZONE })
    const end = DateTime.fromISO(period.endDate, { zone: END_ZONE }).endOf(
      'day'
    )
    if (!start.isValid || !end.isValid) continue
    if (now >= start && now <= end) return period
  }
  return null
}

/**
 * Pure resolver: given the raw `sign-up-promos` flag value, a plan tier, and the
 * current time, returns the tier's marketing `payrollPromoText` from the active
 * promo period when that tier is granted a payroll coupon and defines copy.
 * Returns `undefined` (caller shows the default add-on line) when no promo
 * applies or the tier has no `payrollPromoText` — so copy is driven entirely by
 * the flag and needs no deploy to change.
 */
export const resolvePayrollPromoText = (
  flagValue: unknown,
  tier: string | undefined,
  now: DateTime
): string | undefined => {
  if (!tier) return undefined
  const period = findActivePeriod(flagValue, now)
  const tierCoupon = period?.tierCoupons?.[tier]
  if (!tierCoupon?.payroll) return undefined
  return tierCoupon.payrollPromoText || undefined
}

export interface PayrollSignupPromo {
  /** Promo bullet copy for a plan tier, or `undefined` when no promo applies. */
  payrollPromoTextForTier: (tier?: string) => string | undefined
}

export const usePayrollSignupPromo = (): PayrollSignupPromo => {
  const { value } = useFlag(FEATURE_FLAG_KEYS.signUpPromos, [])
  return {
    payrollPromoTextForTier: (tier?: string) =>
      resolvePayrollPromoText(value, tier, DateTime.now()),
  }
}
